import React, { useState, useEffect, useMemo } from 'react';
import { logger } from '../lib/logger';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { format, addDays } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { Calendar as CalendarIcon, Car, User, CreditCard, ChevronLeft, ChevronRight, Check, AlertCircle, Clock, Tag, X, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import api, { reservationsApi, paymentsApi } from '../lib/api';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import ExtrasSelector from '../components/ExtrasSelector';
import { HybridSelect } from '../components/HybridSelect';
import { HybridDatePicker } from '../components/HybridDatePicker';

// Generate time slots (every 15 minutes) for the configured operating window.
// Bounds come from the admin's pricing config and support minute precision
// (e.g. 23:45). endHour === 24 represents midnight (00:00) — added as the
// very last selectable option.
const generateTimeOptions = (startHour = 4, startMinute = 0, endHour = 24, endMinute = 0) => {
  const safeStartH = Math.max(0, Math.min(23, Number(startHour) || 0));
  const safeStartM = Math.max(0, Math.min(59, Number(startMinute) || 0));
  let safeEndH = Math.max(1, Math.min(24, Number(endHour) || 24));
  let safeEndM = Math.max(0, Math.min(59, Number(endMinute) || 0));
  if (safeEndH === 24) safeEndM = 0;

  const startTotal = safeStartH * 60 + safeStartM;
  const endTotal = safeEndH * 60 + safeEndM;
  if (endTotal <= startTotal) return ['00:00'];

  const options = [];
  // Snap the first slot to the next 15-min boundary >= start
  const firstSlot = Math.ceil(startTotal / 15) * 15;
  // Always include the exact start (e.g. 23:45) even if not multiple of 15
  if (firstSlot !== startTotal) {
    const h = Math.floor(startTotal / 60);
    const m = startTotal % 60;
    options.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
  for (let t = firstSlot; t < endTotal && t < 24 * 60; t += 15) {
    const h = Math.floor(t / 60);
    const m = t % 60;
    options.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
  // Always include the exact end if not midnight (e.g. allow picking 23:45 as
  // departure when end=23:45).
  if (safeEndH < 24) {
    const exactEnd = `${String(safeEndH).padStart(2, '0')}:${String(safeEndM).padStart(2, '0')}`;
    if (!options.includes(exactEnd)) options.push(exactEnd);
  }
  // If the window closes at midnight, add 00:00 as the last slot
  if (safeEndH >= 24) {
    options.push('00:00');
  }
  // Deduplicate keeping insertion order
  return Array.from(new Set(options));
};

// Default global time options (used as fallback before the operating-hours
// config loads from the backend). The actual filtering is done per-instance
// using the `timeOptions` useMemo inside the BookingPage component.
const TIME_OPTIONS = generateTimeOptions();

const BookingPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Prevent double submission
  const [priceData, setPriceData] = useState(null);


  // Form data with date and time
  const [startDate, setStartDate] = useState(() => new Date());
  const [startTime, setStartTime] = useState(''); // Empty - user must select
  const [endDate, setEndDate] = useState(() => addDays(new Date(), 1));
  const [endTime, setEndTime] = useState(''); // Empty - user must select
  const [plate, setPlate] = useState('');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [color, setColor] = useState('');
  const [vehicleType, setVehicleType] = useState('car'); // 'car' or 'van'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('TPV');
  const [arrivalTerminal, setArrivalTerminal] = useState('');
  const [departureTerminal, setDepartureTerminal] = useState('');
  const [arrivalFlight, setArrivalFlight] = useState('');
  const [departureFlight, setDepartureFlight] = useState('');
  const [consentTerms, setConsentTerms] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);

  // Abandoned booking tracking (alerta a admin si no completa la reserva)
  const attemptIdRef = React.useRef(null);
  if (!attemptIdRef.current) {
    const stored = sessionStorage.getItem('pp_attempt_id');
    attemptIdRef.current = stored || (window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `att-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
    sessionStorage.setItem('pp_attempt_id', attemptIdRef.current);
  }

  // Pre-rellenar desde enlace de recuperación de reserva abandonada (?sd=...&st=...&dc=...)
  useEffect(() => {
    const qp = new URLSearchParams(window.location.search);
    const sd = qp.get('sd');
    if (!sd && !qp.get('e')) return;
    const parseDay = (s) => {
      const [y, m, d] = (s || '').split('-').map(Number);
      return y && m && d ? new Date(y, m - 1, d) : null;
    };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sdDate = parseDay(sd);
    const edDate = parseDay(qp.get('ed'));
    if (sdDate && sdDate >= today) {
      setStartDate(sdDate);
      if (edDate && edDate >= sdDate) setEndDate(edDate);
      if (qp.get('st')) setStartTime(qp.get('st'));
      if (qp.get('et')) setEndTime(qp.get('et'));
    }
    if (qp.get('n')) setName(qp.get('n'));
    if (qp.get('e')) setEmail(qp.get('e'));
    if (qp.get('p')) setPhone(qp.get('p'));
    const dc = qp.get('dc');
    if (dc) {
      setDiscountCode(dc.toUpperCase());
      // Auto-validar el código del email de recuperación
      api.post('/marketing/validate-discount', { code: dc.toUpperCase() })
        .then((res) => { if (res.data?.valid) setDiscountData(res.data); })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Solo trackear intentos con intención real (alguna hora elegida),
    // ya que las fechas vienen preseleccionadas por defecto
    if (!startDate || !endDate || (!startTime && !endTime)) return;
    const timer = setTimeout(() => {
      api.post('/booking-attempts/track', {
        attempt_id: attemptIdRef.current,
        start_dt: `${format(startDate, 'yyyy-MM-dd')} ${startTime || ''}`.trim(),
        end_dt: `${format(endDate, 'yyyy-MM-dd')} ${endTime || ''}`.trim(),
        customer_name: name || undefined,
        customer_email: email || undefined,
        customer_phone: phone || undefined,
        step: `Paso ${step}`
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, startTime, endTime, name, email, phone, step]);
  
  // Invoice/billing states
  const [wantsInvoice, setWantsInvoice] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [companyCIF, setCompanyCIF] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  
  // Discount code states
  const [discountCode, setDiscountCode] = useState('');
  const [discountData, setDiscountData] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountError, setDiscountError] = useState('');

  // Extras states
  const [availableExtras, setAvailableExtras] = useState([]);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [extrasLoading, setExtrasLoading] = useState(false);

  // Operating hours loaded from backend (admin can change them live).
  const [operatingHours, setOperatingHours] = useState({ start: 4, startMin: 0, end: 24, endMin: 0 });
  const timeOptions = useMemo(
    () => generateTimeOptions(operatingHours.start, operatingHours.startMin, operatingHours.end, operatingHours.endMin),
    [operatingHours.start, operatingHours.startMin, operatingHours.end, operatingHours.endMin]
  );

  useEffect(() => {
    let cancelled = false;
    api.get('/operating-hours')
      .then(res => {
        if (cancelled) return;
        const s = parseInt(res.data?.operating_start_hour, 10);
        const sm = parseInt(res.data?.operating_start_minute, 10);
        const e = parseInt(res.data?.operating_end_hour, 10);
        const em = parseInt(res.data?.operating_end_minute, 10);
        if (Number.isFinite(s) && Number.isFinite(e)) {
          const startTotal = s * 60 + (Number.isFinite(sm) ? sm : 0);
          const endTotal = e * 60 + (Number.isFinite(em) ? em : 0);
          if (endTotal > startTotal) {
            setOperatingHours({
              start: s,
              startMin: Number.isFinite(sm) ? sm : 0,
              end: e,
              endMin: Number.isFinite(em) ? em : 0,
            });
          }
        }
      })
      .catch(() => { /* keep defaults silently */ });
    return () => { cancelled = true; };
  }, []);

  const locale = i18n.language === 'es' ? es : enUS;

  // Minimum hours in advance for booking (6 hours)
  const MIN_HOURS_ADVANCE = 6;
  
  // Get minimum allowed date/time (now + 6 hours)
  const getMinDateTime = () => {
    const now = new Date();
    return new Date(now.getTime() + MIN_HOURS_ADVANCE * 60 * 60 * 1000);
  };

  // Check if a date is valid for booking (today is valid if time allows)
  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    // Past days are always disabled
    if (checkDate < today) return true;
    
    // Today is allowed (time validation happens separately)
    return false;
  };

  // Get available time options based on selected date
  const getAvailableStartTimes = () => {
    if (!startDate) return timeOptions;
    
    const selectedDate = new Date(startDate);
    const today = new Date();
    
    // If selected date is today, filter times based on MIN_HOURS_ADVANCE
    if (selectedDate.toDateString() === today.toDateString()) {
      const minTime = getMinDateTime();
      const minHour = minTime.getHours();
      const minMinutes = minTime.getMinutes();
      
      const availableTimes = timeOptions.filter(time => {
        const [h, m] = time.split(':').map(Number);
        // Handle midnight (00:00) - it's for the next day conceptually
        if (h === 0 && m === 0) return true;
        // Time must be at least MIN_HOURS_ADVANCE from now
        return h > minHour || (h === minHour && m >= minMinutes);
      });
      
      // If no times available today, return empty to force selecting tomorrow
      return availableTimes.length > 0 ? availableTimes : timeOptions;
    }
    
    return timeOptions;
  };

  // Auto-fill user data if logged in (only on mount/auth change)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const loadUserData = async () => {
      if (isAuthenticated && user) {
        // Set basic data from auth context
        if (user.name && !name) setName(user.name);
        if (user.email && !email) setEmail(user.email);
        if (user.phone && !phone) setPhone(user.phone);
        
        // Load saved vehicle data
        try {
          const response = await api.get('/reservations/user/saved-vehicle');
          const data = response.data;
          if (data.vehicle_plate && !plate) setPlate(data.vehicle_plate);
          if (data.vehicle_brand && !brand) setBrand(data.vehicle_brand);
          if (data.vehicle_model && !model) setModel(data.vehicle_model);
          if (data.vehicle_color && !color) setColor(data.vehicle_color);
        } catch (err) {
          // User doesn't have saved vehicle data - continue with empty fields
          console.debug('No saved vehicle data:', err.message);
        }
      }
    };
    
    loadUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  // Check for cancelled payment
  useEffect(() => {
    if (searchParams.get('cancelled') === 'true') {
      toast.error(i18n.language === 'es' ? 'Pago cancelado' : 'Payment cancelled');
    }
  }, [searchParams, i18n.language]);

  // Load available extras
  useEffect(() => {
    const loadExtras = async () => {
      try {
        setExtrasLoading(true);
        const response = await api.get('/extras');
        setAvailableExtras(response.data.extras || []);
      } catch (error) {
        console.error('Error loading extras:', error);
      } finally {
        setExtrasLoading(false);
      }
    };
    loadExtras();
  }, []);

  // Toggle extra selection
  const handleToggleExtra = (extraId) => {
    setSelectedExtras(prev => {
      if (prev.includes(extraId)) {
        return prev.filter(id => id !== extraId);
      } else {
        return [...prev, extraId];
      }
    });
  };

  // Calculate extras total
  const getExtrasTotal = () => {
    return availableExtras
      .filter(extra => selectedExtras.includes(extra.id))
      .reduce((sum, extra) => sum + extra.price_cents, 0);
  };

  // Calculate price when dates/times change
  useEffect(() => {
    const calculatePrice = async () => {
      // Need both dates AND times to calculate price
      if (!startDate || !endDate || !startTime || !endTime) {
        setPriceData(null);
        return;
      }
      
      const startDt = new Date(startDate);
      const [startH, startM] = startTime.split(':');
      startDt.setHours(parseInt(startH), parseInt(startM), 0, 0);
      
      const endDt = new Date(endDate);
      const [endH, endM] = endTime.split(':');
      endDt.setHours(parseInt(endH), parseInt(endM), 0, 0);
      
      if (endDt <= startDt) {
        setPriceData(null);
        return;
      }

      // Format as local ISO string to preserve the user's intended time
      const toLocalISO = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:00`;
      };

      try {
        const response = await reservationsApi.calculatePrice(
          toLocalISO(startDt),
          toLocalISO(endDt)
        );
        setPriceData(response.data);
      } catch (error) {
        logger.error('Error calculating price:', error);
      }
    };

    calculatePrice();
  }, [startDate, endDate, startTime, endTime]);

  const getFullDates = () => {
    if (!startTime || !endTime) return null;
    
    const startDt = new Date(startDate);
    const [startH, startM] = startTime.split(':');
    startDt.setHours(parseInt(startH), parseInt(startM), 0, 0);
    
    const endDt = new Date(endDate);
    const [endH, endM] = endTime.split(':');
    endDt.setHours(parseInt(endH), parseInt(endM), 0, 0);
    
    return { startDt, endDt };
  };

  // Format date as local ISO string (without UTC conversion)
  const toLocalISOString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  // Validate discount code
  const validateDiscountCode = async () => {
    if (!discountCode.trim()) {
      setDiscountError(i18n.language === 'es' ? 'Introduce un código' : 'Enter a code');
      return;
    }
    
    setDiscountLoading(true);
    setDiscountError('');
    setDiscountData(null);
    
    try {
      const response = await api.post('/marketing/validate-discount', { 
        code: discountCode.trim().toUpperCase() 
      });
      
      if (response.data && response.data.valid) {
        setDiscountData(response.data);
        setDiscountError('');
      } else {
        setDiscountError(response.data?.message || (i18n.language === 'es' ? 'Código no válido' : 'Invalid code'));
        setDiscountData(null);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || error.response?.data?.message;
      setDiscountError(msg || (i18n.language === 'es' ? 'Código no válido o expirado' : 'Invalid or expired code'));
      setDiscountData(null);
    } finally {
      setDiscountLoading(false);
    }
  };

  const removeDiscount = () => {
    setDiscountCode('');
    setDiscountData(null);
    setDiscountError('');
  };

  // Calculate final price with discount and van surcharge
  const getFinalPrice = () => {
    if (!priceData?.valid) return null;
    
    let baseCents = priceData.total_cents || (priceData.total_euros * 100);
    let vanSurchargeCents = 0;
    
    // Apply van surcharge (30%)
    if (vehicleType === 'van') {
      vanSurchargeCents = Math.round(baseCents * 0.30);
    }
    
    // Calculate extras total
    const extrasCents = getExtrasTotal();
    
    let totalCents = baseCents + vanSurchargeCents + extrasCents;
    let discountCents = 0;
    
    if (discountData?.valid) {
      if (discountData.type === 'PERCENTAGE') {
        // Discount applies to base + van, NOT extras
        discountCents = Math.round((baseCents + vanSurchargeCents) * discountData.value / 100);
      } else if (discountData.type === 'FIXED') {
        discountCents = discountData.value * 100;
      }
    }
    
    const finalCents = Math.max(0, totalCents - discountCents);
    
    return {
      baseCents: baseCents,
      baseEuros: baseCents / 100,
      vanSurchargeCents: vanSurchargeCents,
      vanSurchargeEuros: vanSurchargeCents / 100,
      extrasCents: extrasCents,
      extrasEuros: extrasCents / 100,
      originalCents: totalCents,
      originalEuros: totalCents / 100,
      discountCents: discountCents,
      discountEuros: discountCents / 100,
      finalCents: finalCents,
      finalEuros: finalCents / 100,
      hasDiscount: discountCents > 0,
      hasVanSurcharge: vanSurchargeCents > 0,
      hasExtras: extrasCents > 0
    };
  };

  const canProceedStep1 = () => {
    if (!startDate || !endDate || !priceData) return false;
    if (!priceData.valid) return false;
    // Require time selection
    if (!startTime || !endTime) return false;
    return true;
  };

  const validateAndProceedStep1 = () => {
    const errors = [];
    if (!startDate) errors.push('Fecha de entrada');
    if (!endDate) errors.push('Fecha de salida');
    if (!startTime) errors.push('Hora de entrada');
    if (!endTime) errors.push('Hora de salida');
    
    if (errors.length > 0) {
      toast.error(`Completa los campos obligatorios: ${errors.join(', ')}`, {
        duration: 4000
      });
      return false;
    }
    
    if (!priceData || !priceData.valid) {
      toast.error(priceData?.error || 'Error al calcular el precio. Verifica las fechas.', {
        duration: 4000
      });
      return false;
    }
    
    setStep(2);
    return true;
  };

  const canProceedStep2 = () => {
    return plate.trim().length >= 4;
  };

  const validateAndProceedStep2 = () => {
    if (!plate || plate.trim().length < 4) {
      toast.error('Introduce una matrícula válida (mínimo 4 caracteres)', {
        duration: 4000
      });
      return false;
    }
    setStep(3);
    return true;
  };

  // Phone validation helper
  const isValidPhone = (phoneNumber) => {
    if (!phoneNumber || phoneNumber.trim() === '') return true; // Phone is optional
    // Remove spaces, dashes, parentheses
    const cleaned = phoneNumber.replace(/[\s\-\(\)]/g, '');
    // Must be 9-15 digits, optionally starting with +
    return /^\+?\d{9,15}$/.test(cleaned);
  };

  const canProceedStep3 = () => {
    const hasName = name.trim().length >= 2;
    const hasValidEmail = email.includes('@') && email.includes('.');
    const hasTermsConsent = consentTerms;
    const phoneValid = isValidPhone(phone);
    
    // If wants invoice, require billing data
    if (wantsInvoice) {
      const hasCompanyName = companyName.trim().length >= 2;
      const hasCIF = companyCIF.trim().length >= 8;
      const hasAddress = companyAddress.trim().length >= 5;
      if (!hasCompanyName || !hasCIF || !hasAddress) return false;
    }
    
    return hasName && hasValidEmail && hasTermsConsent && phoneValid;
  };

  const validateAndProceedStep3 = () => {
    const errors = [];
    
    if (!name || name.trim().length < 2) errors.push('Nombre completo');
    if (!email || !email.includes('@') || !email.includes('.')) errors.push('Email válido');
    if (phone && !isValidPhone(phone)) errors.push('Teléfono válido');
    if (!consentTerms) errors.push('Aceptar términos y condiciones');
    
    // Invoice validation
    if (wantsInvoice) {
      if (!companyName || companyName.trim().length < 2) errors.push('Nombre de empresa');
      if (!companyCIF || companyCIF.trim().length < 8) errors.push('CIF/NIF válido');
      if (!companyAddress || companyAddress.trim().length < 5) errors.push('Dirección fiscal');
    }
    
    if (errors.length > 0) {
      toast.error(`Completa los campos obligatorios: ${errors.join(', ')}`, {
        duration: 5000
      });
      return false;
    }
    
    setStep(4);
    return true;
  };

  const handleSubmit = async () => {
    // Prevent double submission
    if (isSubmitting) {
      logger.warn('Submission already in progress, ignoring duplicate click');
      return;
    }
    
    if (!canProceedStep3()) return;
    
    // Set submitting flag IMMEDIATELY before any async operation
    setIsSubmitting(true);
    setLoading(true);
    
    try {
      const { startDt, endDt } = getFullDates();
      
      // Calculate final price with discount if applicable
      const finalPrice = getFinalPrice();
      
      const reservationData = {
        start_dt: toLocalISOString(startDt),
        end_dt: toLocalISOString(endDt),
        vehicle_plate: plate.toUpperCase(),
        vehicle_brand: brand || null,
        vehicle_model: model || null,
        vehicle_color: color || null,
        vehicle_type: vehicleType, // 'car' or 'van'
        customer_name: name,
        customer_email: email,
        customer_phone: phone || null,
        payment_method: paymentMethod,
        notes: notes || null,
        arrival_terminal: arrivalTerminal || null,
        departure_terminal: departureTerminal || null,
        arrival_flight: arrivalFlight || null,
        departure_flight: departureFlight || null,
        is_guest: !isAuthenticated,
        language: i18n.language,
        consent_marketing: consentMarketing,
        discount_code: discountData?.valid ? discountData.code : null,
        discount_amount_cents: finalPrice?.discountCents || 0,
        // Invoice/billing data
        wants_invoice: wantsInvoice,
        billing_company_name: wantsInvoice ? companyName : null,
        billing_cif: wantsInvoice ? companyCIF : null,
        billing_address: wantsInvoice ? companyAddress : null,
        // Extras seleccionados
        selected_extras: selectedExtras
      };

      const response = await reservationsApi.create(reservationData);
      const reservation = response.data;

      // Marcar intento como completado (evita alerta de reserva abandonada)
      api.post('/booking-attempts/complete', { attempt_id: attemptIdRef.current }).catch(() => {});
      sessionStorage.removeItem('pp_attempt_id');

      // Check if this was a duplicate that was blocked
      if (reservation.duplicate_blocked) {
        logger.warn('Duplicate reservation detected, redirecting to existing reservation');
        toast.info(reservation.message || 'Ya tienes una reserva para estas fechas');
        // Redirect to the existing reservation
        navigate(`/checkout/success?reservation_id=${reservation.id}&method=${paymentMethod.toLowerCase()}&duplicate=true`);
        return;
      }

      if (paymentMethod === 'STRIPE') {
        // Create Stripe checkout session (disabled - now all payments at parking)
        const checkoutResponse = await paymentsApi.createCheckoutSession(
          reservation.id,
          window.location.origin
        );
        // Redirect to Stripe
        window.location.href = checkoutResponse.data.url;
      } else {
        // TPV/Bizum or Cash - both pay at parking
        navigate(`/checkout/success?reservation_id=${reservation.id}&method=${paymentMethod.toLowerCase()}`);
      }
    } catch (error) {
      logger.error('Error creating reservation:', error);
      toast.error(error.response?.data?.detail || 'Error al crear la reserva');
      // Only reset isSubmitting on error, not on success (we navigate away)
      setIsSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, label: t('booking.step1'), icon: CalendarIcon },
    { num: 2, label: t('booking.step2'), icon: Car },
    { num: 3, label: t('booking.step3'), icon: User },
    { num: 4, label: t('booking.step4'), icon: CreditCard },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-heavy sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
              <Car className="w-6 h-6 text-black" />
            </div>
            <span className="font-heading font-bold text-xl text-white">PLEYADES</span>
          </Link>
          <span className="text-zinc-400 text-sm">{t('booking.title')}</span>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {steps.map((s, idx) => (
              <React.Fragment key={s.num}>
                <div 
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-sm transition-colors",
                    step === s.num ? "bg-primary text-black" : "bg-secondary text-zinc-400"
                  )}
                  data-testid={`step-indicator-${s.num}`}
                >
                  <s.icon className="w-4 h-4" />
                  <span className="hidden sm:inline text-sm font-medium">{s.label}</span>
                </div>
                {idx < steps.length - 1 && (
                  <ChevronRight className="w-4 h-4 text-zinc-600" />
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <AnimatePresence mode="wait">
                {/* Step 1: Dates */}
                {step === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="card-dark p-6"
                  >
                    <h2 className="font-heading text-2xl font-bold text-white mb-6">
                      {t('booking.step1')}: {t('booking.startDate')} & {t('booking.endDate')}
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Start Date & Time */}
                      <div className="space-y-3">
                        <Label className="text-white">{t('booking.startDate')}</Label>
                        <HybridDatePicker
                          value={startDate}
                          onSelect={(date) => {
                            setStartDate(date);
                            // Si la fecha de salida es anterior a la de entrada, resetearla
                            if (endDate && date && date >= endDate) {
                              setEndDate(null);
                            }
                          }}
                          disabled={isDateDisabled}
                          minDate={new Date()}
                          locale={locale}
                          className="input-gold"
                          testId="start-date-picker"
                        />
                        
                        {/* Start Time - Required */}
                        <div className="space-y-1">
                          <Label className="text-sm text-zinc-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Hora de entrada <span className="text-[#FFD700]">*</span>
                          </Label>
                          <HybridSelect
                            value={startTime}
                            onValueChange={setStartTime}
                            options={getAvailableStartTimes().map((time) => ({ value: time, label: time }))}
                            placeholder="⏰ Selecciona hora..."
                            className={cn(
                              "input-gold",
                              !startTime && "border-[#FFD700]/50 animate-pulse"
                            )}
                            testId="start-time-select"
                          />
                          {!startTime && startDate && (
                            <p className="text-xs text-[#FFD700] mt-1">⚠️ Selecciona la hora de entrada</p>
                          )}
                        </div>
                      </div>

                      {/* End Date & Time */}
                      <div className="space-y-3">
                        <Label className="text-white">{t('booking.endDate')}</Label>
                        <HybridDatePicker
                          value={endDate}
                          onSelect={setEndDate}
                          disabled={(date) => startDate && date < startDate}
                          minDate={startDate || new Date()}
                          locale={locale}
                          className="input-gold"
                          testId="end-date-picker"
                        />
                        
                        {/* End Time - Required */}
                        <div className="space-y-1">
                          <Label className="text-sm text-zinc-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Hora de salida <span className="text-[#FFD700]">*</span>
                          </Label>
                          <HybridSelect
                            value={endTime}
                            onValueChange={setEndTime}
                            options={timeOptions.map((time) => ({ value: time, label: time }))}
                            placeholder="⏰ Selecciona hora..."
                            className={cn(
                              "input-gold",
                              !endTime && "border-[#FFD700]/50 animate-pulse"
                            )}
                            testId="end-time-select"
                          />
                          {!endTime && endDate && (
                            <p className="text-xs text-[#FFD700] mt-1">⚠️ Selecciona la hora de salida</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Message when times are not selected */}
                    {startDate && endDate && (!startTime || !endTime) && (
                      <div className="mt-4 p-4 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-lg flex items-center gap-3">
                        <Clock className="w-5 h-5 text-[#FFD700]" />
                        <span className="text-[#FFD700]">
                          Selecciona las horas de entrada y salida para ver el precio
                        </span>
                      </div>
                    )}

                    {priceData && !priceData.valid && (
                      <div className="mt-4 p-4 bg-destructive/10 border border-destructive/30 rounded-sm flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 text-destructive" />
                        <span className="text-destructive">{priceData.error}</span>
                      </div>
                    )}

                    <div className="mt-6 flex justify-end">
                      <Button
                        onClick={validateAndProceedStep1}
                        className="btn-primary"
                        data-testid="step1-continue"
                      >
                        {t('booking.continue')}
                        <ChevronRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </motion.div>
                )}

                {/* Step 2: Vehicle */}
                {step === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="card-dark p-6"
                  >
                    <h2 className="font-heading text-2xl font-bold text-white mb-6">
                      {t('booking.step2')}: {t('booking.plate')}
                    </h2>

                    <div className="space-y-4">
                      <div>
                        <Label className="text-white">{t('booking.plate')} *</Label>
                        <Input
                          value={plate}
                          onChange={(e) => setPlate(e.target.value.toUpperCase())}
                          onInput={(e) => setPlate(e.target.value.toUpperCase())}
                          placeholder="1234 ABC"
                          className="input-gold font-mono text-lg tracking-wider"
                          autoComplete="off"
                          data-testid="plate-input"
                        />
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-white">{t('booking.brand')}</Label>
                          <Input
                            value={brand}
                            onChange={(e) => setBrand(e.target.value)}
                            placeholder="Toyota"
                            className="input-gold"
                            data-testid="brand-input"
                          />
                        </div>
                        <div>
                          <Label className="text-white">{t('booking.model')}</Label>
                          <Input
                            value={model}
                            onChange={(e) => setModel(e.target.value)}
                            placeholder="Corolla"
                            className="input-gold"
                            data-testid="model-input"
                          />
                        </div>
                        <div>
                          <Label className="text-white">{t('booking.color')}</Label>
                          <Input
                            value={color}
                            onChange={(e) => setColor(e.target.value)}
                            placeholder="Blanco"
                            className="input-gold"
                            data-testid="color-input"
                          />
                        </div>
                      </div>

                      {/* Vehicle Type Selector */}
                      <div>
                        <Label className="text-white mb-3 block">
                          {i18n.language === 'es' ? 'Tipo de vehículo' : 'Vehicle type'} *
                        </Label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => setVehicleType('car')}
                            className={cn(
                              "p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2",
                              vehicleType === 'car' 
                                ? "border-primary bg-primary/10 text-primary" 
                                : "border-white/20 bg-white/5 text-zinc-400 hover:border-white/40"
                            )}
                            data-testid="vehicle-type-car"
                          >
                            <Car className="w-8 h-8" />
                            <span className="font-medium">
                              {i18n.language === 'es' ? 'Coche / SUV' : 'Car / SUV'}
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setVehicleType('van')}
                            className={cn(
                              "p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 relative",
                              vehicleType === 'van' 
                                ? "border-primary bg-primary/10 text-primary" 
                                : "border-white/20 bg-white/5 text-zinc-400 hover:border-white/40"
                            )}
                            data-testid="vehicle-type-van"
                          >
                            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 14h4v5H1zM5 14h9V9H5zM14 14h4l2-5h-6zM19 14h3v5h-3z"/>
                              <circle cx="6" cy="19" r="2"/>
                              <circle cx="18" cy="19" r="2"/>
                              <path d="M5 9V6a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v3"/>
                            </svg>
                            <span className="font-medium">
                              {i18n.language === 'es' ? 'Furgoneta' : 'Van'}
                            </span>
                            <span className="absolute -top-2 -right-2 bg-amber-500 text-black text-xs font-bold px-2 py-0.5 rounded-full">
                              +30%
                            </span>
                          </button>
                        </div>
                        {vehicleType === 'van' && (
                          <p className="text-amber-400 text-sm mt-2 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {i18n.language === 'es' 
                              ? 'Las furgonetas tienen un recargo del 30% sobre el precio base' 
                              : 'Vans have a 30% surcharge on the base price'}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="mt-6 flex justify-between">
                      <Button
                        onClick={() => setStep(1)}
                        variant="ghost"
                        className="btn-ghost"
                        data-testid="step2-back"
                      >
                        <ChevronLeft className="mr-2 w-4 h-4" />
                        {t('booking.back')}
                      </Button>
                      <Button
                        onClick={validateAndProceedStep2}
                        className="btn-primary"
                        data-testid="step2-continue"
                      >
                        {t('booking.continue')}
                        <ChevronRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Contact */}
                {step === 3 && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="card-dark p-6"
                  >
                    <h2 className="font-heading text-2xl font-bold text-white mb-6">
                      {t('booking.step3')}: {t('booking.name')}
                    </h2>

                    <div className="space-y-4">
                      <div>
                        <Label className="text-white">{t('booking.name')} *</Label>
                        <Input
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Juan García"
                          className="input-gold"
                          data-testid="name-input"
                        />
                      </div>

                      <div>
                        <Label className="text-white">{t('booking.email')} *</Label>
                        <Input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="juan@email.com"
                          className="input-gold"
                          data-testid="email-input"
                        />
                      </div>

                      <div>
                        <Label className="text-white">{t('booking.phone')}</Label>
                        <Input
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="+34 600 000 000"
                          className={cn(
                            "input-gold",
                            phone && !isValidPhone(phone) && "border-red-500 focus:border-red-500"
                          )}
                          data-testid="phone-input"
                        />
                        {phone && !isValidPhone(phone) && (
                          <p className="text-xs text-red-400 mt-1">
                            {i18n.language === 'es' 
                              ? 'Formato de teléfono no válido (ej: +34 600 000 000)' 
                              : 'Invalid phone format (e.g.: +34 600 000 000)'}
                          </p>
                        )}
                      </div>

                      {/* Terminal Selection */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-white">
                            {i18n.language === 'es' ? 'Terminal de recogida' : 'Pickup Terminal'}
                          </Label>
                          <HybridSelect
                            value={arrivalTerminal}
                            onValueChange={setArrivalTerminal}
                            options={[
                              { value: 'T1', label: 'Terminal 1 (T1)' },
                              { value: 'T2', label: 'Terminal 2 (T2)' },
                              { value: 'T4', label: 'Terminal 4 (T4)' },
                            ]}
                            placeholder={i18n.language === 'es' ? 'Seleccionar' : 'Select'}
                            className="input-gold"
                            testId="arrival-terminal-select"
                          />
                        </div>
                        <div>
                          <Label className="text-white">
                            {i18n.language === 'es' ? 'Terminal de devolución' : 'Return Terminal'}
                          </Label>
                          <HybridSelect
                            value={departureTerminal}
                            onValueChange={setDepartureTerminal}
                            options={[
                              { value: 'T1', label: 'Terminal 1 (T1)' },
                              { value: 'T2', label: 'Terminal 2 (T2)' },
                              { value: 'T4', label: 'Terminal 4 (T4)' },
                            ]}
                            placeholder={i18n.language === 'es' ? 'Seleccionar' : 'Select'}
                            className="input-gold"
                            testId="departure-terminal-select"
                          />
                        </div>
                      </div>

                      {/* Flight Numbers - Required */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-white">
                            {i18n.language === 'es' ? 'Nº Vuelo de salida' : 'Departure Flight No.'}
                          </Label>
                          <Input
                            type="text"
                            value={arrivalFlight}
                            onChange={(e) => setArrivalFlight(e.target.value.toUpperCase())}
                            placeholder="IB1234"
                            className="input-gold"
                            data-testid="arrival-flight-input"
                          />
                          <p className="text-xs text-zinc-500 mt-1">
                            {i18n.language === 'es' 
                              ? 'Vuelo con el que sales de viaje' 
                              : 'Flight you depart with'}
                          </p>
                        </div>
                        <div>
                          <Label className="text-white">
                            {i18n.language === 'es' ? 'Nº Vuelo de regreso' : 'Return Flight No.'}
                          </Label>
                          <Input
                            type="text"
                            value={departureFlight}
                            onChange={(e) => setDepartureFlight(e.target.value.toUpperCase())}
                            placeholder="IB5678"
                            className="input-gold"
                            data-testid="departure-flight-input"
                          />
                          <p className="text-xs text-zinc-500 mt-1">
                            {i18n.language === 'es' 
                              ? 'Vuelo con el que regresas' 
                              : 'Flight you return with'}
                          </p>
                        </div>
                      </div>

                      <div>
                        <Label className="text-white">{t('booking.notes')}</Label>
                        <Textarea
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="Instrucciones especiales..."
                          className="input-gold"
                          data-testid="notes-input"
                        />
                      </div>

                      {/* Invoice Request Section */}
                      <div className="mt-4 p-4 bg-white/5 rounded-lg border border-white/10">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            id="wants-invoice"
                            checked={wantsInvoice}
                            onChange={(e) => setWantsInvoice(e.target.checked)}
                            className="mt-1 w-4 h-4 accent-[#FFD700]"
                            data-testid="wants-invoice"
                          />
                          <div>
                            <label htmlFor="wants-invoice" className="text-white font-medium cursor-pointer">
                              {i18n.language === 'es' 
                                ? '¿Deseas solicitar factura?' 
                                : 'Do you want to request an invoice?'}
                            </label>
                            <p className="text-xs text-zinc-500 mt-1">
                              {i18n.language === 'es' 
                                ? 'Marca esta opción si necesitas factura con datos fiscales' 
                                : 'Check this option if you need an invoice with tax details'}
                            </p>
                          </div>
                        </div>
                        
                        {/* Invoice Form - Only shown when checkbox is checked */}
                        {wantsInvoice && (
                          <div className="mt-4 pt-4 border-t border-white/10 space-y-4">
                            <div>
                              <Label className="text-white">
                                {i18n.language === 'es' ? 'Nombre de la empresa' : 'Company Name'} <span className="text-[#FFD700]">*</span>
                              </Label>
                              <Input
                                type="text"
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                                placeholder={i18n.language === 'es' ? 'Ej: Mi Empresa S.L.' : 'E.g.: My Company Ltd.'}
                                className="input-gold"
                                data-testid="company-name-input"
                              />
                            </div>
                            <div>
                              <Label className="text-white">
                                CIF/NIF <span className="text-[#FFD700]">*</span>
                              </Label>
                              <Input
                                type="text"
                                value={companyCIF}
                                onChange={(e) => setCompanyCIF(e.target.value.toUpperCase())}
                                placeholder="B12345678"
                                className="input-gold"
                                data-testid="company-cif-input"
                              />
                            </div>
                            <div>
                              <Label className="text-white">
                                {i18n.language === 'es' ? 'Dirección fiscal' : 'Billing Address'} <span className="text-[#FFD700]">*</span>
                              </Label>
                              <Textarea
                                value={companyAddress}
                                onChange={(e) => setCompanyAddress(e.target.value)}
                                placeholder={i18n.language === 'es' ? 'Calle, número, código postal, ciudad...' : 'Street, number, postal code, city...'}
                                className="input-gold"
                                rows={2}
                                data-testid="company-address-input"
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Extras Selector */}
                      {availableExtras.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-white/10">
                          <ExtrasSelector
                            extras={availableExtras}
                            selectedExtras={selectedExtras}
                            onToggleExtra={handleToggleExtra}
                            loading={extrasLoading}
                          />
                        </div>
                      )}

                      {/* Consent checkboxes */}
                      <div className="space-y-3 mt-4 pt-4 border-t border-white/10">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            id="consent-terms"
                            checked={consentTerms}
                            onChange={(e) => setConsentTerms(e.target.checked)}
                            className="mt-1 w-4 h-4 accent-primary"
                            data-testid="consent-terms"
                          />
                          <label htmlFor="consent-terms" className="text-sm text-zinc-300">
                            {i18n.language === 'es' 
                              ? <>Acepto los <a href="/terminos" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">términos y condiciones</a> y la <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">política de privacidad</a> <span className="text-red-400">*</span></>
                              : <>I accept the <a href="/terminos" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">terms and conditions</a> and <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">privacy policy</a> <span className="text-red-400">*</span></>}
                          </label>
                        </div>
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            id="consent-marketing"
                            checked={consentMarketing}
                            onChange={(e) => setConsentMarketing(e.target.checked)}
                            className="mt-1 w-4 h-4 accent-primary"
                            data-testid="consent-marketing"
                          />
                          <label htmlFor="consent-marketing" className="text-sm text-zinc-500">
                            {i18n.language === 'es' 
                              ? 'Quiero recibir ofertas y promociones exclusivas por email (opcional)'
                              : 'I want to receive exclusive offers and promotions by email (optional)'}
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 flex justify-between">
                      <Button
                        onClick={() => setStep(2)}
                        variant="ghost"
                        className="btn-ghost"
                        data-testid="step3-back"
                      >
                        <ChevronLeft className="mr-2 w-4 h-4" />
                        {t('booking.back')}
                      </Button>
                      <Button
                        onClick={validateAndProceedStep3}
                        className="btn-primary"
                        data-testid="step3-continue"
                      >
                        {t('booking.continue')}
                        <ChevronRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </motion.div>
                )}

                {/* Step 4: Payment */}
                {step === 4 && (
                  <motion.div
                    key="step4"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="card-dark p-6"
                  >
                    <h2 className="font-heading text-2xl font-bold text-white mb-6">
                      {t('booking.step4')}: {t('booking.paymentMethod')}
                    </h2>

                    <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-4">
                      <div 
                        className={cn(
                          "flex items-center gap-4 p-4 rounded-sm border cursor-pointer transition-colors",
                          paymentMethod === 'TPV' ? "border-primary bg-primary/10" : "border-white/10 bg-secondary"
                        )}
                        onClick={() => setPaymentMethod('TPV')}
                        data-testid="payment-tpv"
                      >
                        <RadioGroupItem value="TPV" id="tpv" />
                        <div className="flex-1">
                          <Label htmlFor="tpv" className="text-white font-medium cursor-pointer">
                            <CreditCard className="inline w-5 h-5 mr-2 text-primary" />
                            {i18n.language === 'es' ? 'Tarjeta o Bizum' : 'Card or Bizum'}
                          </Label>
                          <p className="text-sm text-zinc-400 mt-1">
                            {i18n.language === 'es' ? 'Pago en el parking mediante TPV o Bizum' : 'Pay at parking via card terminal or Bizum'}
                          </p>
                        </div>
                      </div>

                      <div 
                        className={cn(
                          "flex items-center gap-4 p-4 rounded-sm border cursor-pointer transition-colors",
                          paymentMethod === 'CASH' ? "border-primary bg-primary/10" : "border-white/10 bg-secondary"
                        )}
                        onClick={() => setPaymentMethod('CASH')}
                        data-testid="payment-cash"
                      >
                        <RadioGroupItem value="CASH" id="cash" />
                        <div className="flex-1">
                          <Label htmlFor="cash" className="text-white font-medium cursor-pointer">
                            {t('booking.cash')}
                          </Label>
                          <p className="text-sm text-zinc-400 mt-1">
                            {i18n.language === 'es' ? 'Pago en efectivo en el parking' : 'Cash payment at parking'}
                          </p>
                        </div>
                      </div>
                    </RadioGroup>

                    {/* Discount Code Section */}
                    <div className="mt-6 p-4 bg-secondary/50 rounded-lg border border-white/5">
                      <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                        <Tag className="w-4 h-4 text-primary" />
                        {i18n.language === 'es' ? 'Código de descuento' : 'Discount code'}
                      </h3>
                      
                      {!discountData ? (
                        <div className="flex gap-2">
                          <Input
                            type="text"
                            value={discountCode}
                            onChange={(e) => {
                              setDiscountCode(e.target.value.toUpperCase());
                              setDiscountError('');
                            }}
                            placeholder={i18n.language === 'es' ? 'Introduce tu código' : 'Enter your code'}
                            className="input-gold flex-1 uppercase"
                            disabled={discountLoading}
                            onKeyDown={(e) => e.key === 'Enter' && validateDiscountCode()}
                            data-testid="discount-code-input"
                          />
                          <Button
                            onClick={validateDiscountCode}
                            disabled={discountLoading || !discountCode.trim()}
                            className="btn-primary px-6"
                            data-testid="apply-discount-btn"
                          >
                            {discountLoading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              i18n.language === 'es' ? 'Aplicar' : 'Apply'
                            )}
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Check className="w-5 h-5 text-green-500" />
                            <div>
                              <span className="text-green-400 font-mono font-bold">{discountData.code}</span>
                              <span className="text-green-300 text-sm ml-2">
                                -{discountData.type === 'PERCENTAGE' 
                                  ? `${discountData.value}%` 
                                  : `${discountData.value}€`}
                              </span>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={removeDiscount}
                            className="text-zinc-400 hover:text-white"
                            data-testid="remove-discount-btn"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      
                      {discountError && (
                        <p className="text-red-400 text-sm mt-2 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {discountError}
                        </p>
                      )}
                    </div>

                    <div className="mt-6 flex justify-between">
                      <Button
                        onClick={() => setStep(3)}
                        variant="ghost"
                        className="btn-ghost"
                        data-testid="step4-back"
                        disabled={isSubmitting}
                      >
                        <ChevronLeft className="mr-2 w-4 h-4" />
                        {t('booking.back')}
                      </Button>
                      <Button
                        onClick={handleSubmit}
                        disabled={loading || isSubmitting}
                        className="btn-primary"
                        data-testid="submit-booking"
                      >
                        {loading || isSubmitting ? (
                          t('common.loading')
                        ) : (
                          <>
                            {t('booking.payOnArrival')}
                            <Check className="ml-2 w-4 h-4" />
                          </>
                        )}
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Summary Sidebar */}
            <div className="lg:col-span-1">
              <div className="card-dark p-6 sticky top-24">
                <h3 className="font-heading text-lg font-bold text-white mb-4">
                  {t('booking.summary')}
                </h3>

                {startDate && endDate && (
                  <div className="space-y-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-400">{t('booking.startDate')}</span>
                      <span className="text-white">
                        {format(startDate, 'dd/MM/yyyy', { locale })} {startTime}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-400">{t('booking.endDate')}</span>
                      <span className="text-white">
                        {format(endDate, 'dd/MM/yyyy', { locale })} {endTime}
                      </span>
                    </div>

                    {plate && (
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-400">{t('booking.plate')}</span>
                        <span className="text-white font-mono">{plate}</span>
                      </div>
                    )}

                    <div className="border-t border-white/10 pt-4">
                      {priceData?.valid && priceData.breakdown && (
                        <>
                          {/* Pricing Mode Indicator */}
                          <div className="text-xs text-zinc-500 mb-3 flex items-center gap-1">
                            <span className={`w-2 h-2 rounded-full ${priceData.breakdown.pricing_mode === 'ADVANCED' ? 'bg-green-500' : 'bg-blue-500'}`}></span>
                            {priceData.breakdown.pricing_mode === 'ADVANCED' 
                              ? (i18n.language === 'es' ? 'Precio por día' : 'Per-day pricing')
                              : (i18n.language === 'es' ? 'Tarifa estándar' : 'Standard rate')
                            }
                          </div>

                          {/* Base Price (if applicable) */}
                          {priceData.breakdown.base_price_cents > 0 && (
                            <div className="flex justify-between text-sm mb-2">
                              <span className="text-zinc-400">
                                {i18n.language === 'es' ? 'Precio base' : 'Base price'}
                              </span>
                              <span className="text-white">
                                {priceData.breakdown.base_price_euros.toFixed(2)}€
                              </span>
                            </div>
                          )}

                          {/* Days Breakdown */}
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-zinc-400">
                              {priceData.breakdown.total_days} {i18n.language === 'es' ? 'día(s)' : 'day(s)'}
                            </span>
                            <span className="text-white">
                              {priceData.breakdown.days_subtotal_euros?.toFixed(2) || (priceData.breakdown.daily_rate_cents / 100 * priceData.breakdown.total_days).toFixed(2)}€
                            </span>
                          </div>

                          {/* Van Surcharge (if applicable) */}
                          {getFinalPrice()?.hasVanSurcharge && (
                            <div className="flex justify-between text-sm mb-2 text-amber-400">
                              <span className="flex items-center gap-1">
                                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M1 14h4v5H1zM5 14h9V9H5zM14 14h4l2-5h-6zM19 14h3v5h-3z"/>
                                  <circle cx="6" cy="19" r="2"/>
                                  <circle cx="18" cy="19" r="2"/>
                                </svg>
                                {i18n.language === 'es' ? 'Recargo furgoneta (+30%)' : 'Van surcharge (+30%)'}
                              </span>
                              <span>+{getFinalPrice().vanSurchargeEuros.toFixed(2)}€</span>
                            </div>
                          )}

                          {/* Extras (if any selected) */}
                          {getFinalPrice()?.hasExtras && (
                            <div className="mb-2">
                              <div className="flex justify-between text-sm text-primary">
                                <span className="flex items-center gap-1">
                                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                  </svg>
                                  {i18n.language === 'es' ? 'Extras' : 'Extras'} ({selectedExtras.length})
                                </span>
                                <span>+{getFinalPrice().extrasEuros.toFixed(2)}€</span>
                              </div>
                              {/* List selected extras */}
                              <div className="mt-1 space-y-0.5">
                                {availableExtras
                                  .filter(extra => selectedExtras.includes(extra.id))
                                  .map(extra => (
                                    <div key={extra.id} className="flex justify-between text-xs text-zinc-500 pl-4">
                                      <span>• {extra.name}</span>
                                      <span>{(extra.price_cents / 100).toFixed(2)}€</span>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}

                          {/* Discount (if applicable) */}
                          {priceData.breakdown.discount_cents > 0 && (
                            <div className="flex justify-between text-sm mb-2 text-green-400">
                              <span>
                                {i18n.language === 'es' ? 'Descuento' : 'Discount'} ({priceData.breakdown.discount_percent}%)
                              </span>
                              <span>-{priceData.breakdown.discount_euros.toFixed(2)}€</span>
                            </div>
                          )}

                          {/* Coupon Discount (if applied) */}
                          {discountData?.valid && getFinalPrice()?.hasDiscount && (
                            <div className="flex justify-between text-sm mb-2 text-green-400">
                              <span className="flex items-center gap-1">
                                <Tag className="w-3 h-3" />
                                {i18n.language === 'es' ? 'Código' : 'Code'} {discountData.code}
                              </span>
                              <span>-{getFinalPrice().discountEuros.toFixed(2)}€</span>
                            </div>
                          )}

                          {/* Total */}
                          <div className="flex justify-between items-center border-t border-white/10 pt-3 mt-3">
                            <span className="font-heading font-bold text-white">{t('booking.total')}</span>
                            <div className="text-right">
                              {(getFinalPrice()?.hasDiscount || getFinalPrice()?.hasVanSurcharge) && (
                                <span className="text-zinc-500 line-through text-sm mr-2">
                                  {priceData.total_euros.toFixed(2)}€
                                </span>
                              )}
                              <span className="font-heading text-2xl font-bold text-primary">
                                {getFinalPrice()?.finalEuros.toFixed(2) || priceData.total_euros.toFixed(2)}€
                              </span>
                            </div>
                          </div>

                          {/* Day-by-day breakdown (collapsed by default) */}
                          {priceData.breakdown.day_breakdown && priceData.breakdown.day_breakdown.length > 1 && (
                            <details className="mt-4">
                              <summary className="text-xs text-zinc-500 cursor-pointer hover:text-zinc-300 transition-colors">
                                {i18n.language === 'es' ? 'Ver desglose por día' : 'View daily breakdown'}
                              </summary>
                              <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                                {priceData.breakdown.day_breakdown.map((day, idx) => (
                                  <div key={`day-${day.date}-${idx}`} className="flex justify-between text-xs">
                                    <span className="text-zinc-500">
                                      {day.date} ({day.weekday_name})
                                    </span>
                                    <span className={`${day.price_type === 'custom' ? 'text-green-400' : 'text-zinc-400'}`}>
                                      {day.price_euros?.toFixed(2) || (day.price_cents / 100).toFixed(2)}€
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {!startDate && !endDate && (
                  <p className="text-zinc-500 text-sm">
                    {i18n.language === 'es' 
                      ? 'Selecciona las fechas para ver el precio' 
                      : 'Select dates to see the price'}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
